import axios from "axios";
import { baseURL } from "../Constants";
import { FantasyTeam } from "../models/FantasyTeam";
import { FantasyStartTeam } from "../models/FantasyStartTeam";
import { Account } from "../models/Account";
import { FantasyPlayerMatch } from "../models/FantasyPlayerMatch";
import { FantasyPickDTO } from "../models/FantasyPickDTO";

const url = `${baseURL}/Fantasy`;
const accountUrl = `${baseURL}/Account`;  // Add URL for Account

export const getAccountBySteamId = async (steamId: string): Promise<Account> => {
    try {
        const response = await axios.get(`${accountUrl}/${steamId}`);  // Make the API request to get account by steamId
        return response.data;  // Return the account object
    } catch (error) {
        throw error;  // Handle the error
    }
};

export const getFantasyPlayerMatchesByTeamId = async (teamId: number): Promise<FantasyPlayerMatch[]> => {
    try {
        const response = await axios.get(`${url}/getFantasyPlayerMatchesByTeamId?teamId=${teamId}`);
        
        // Check if response status is 204 (No Content)
        if (response.status === 204) {
            const error = new Error("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
            (error as any).isProcessing = true;
            throw error;
        }
        
        return response.data;
    }
    catch (error) {
        // If it's an axios error with 204 status, handle it specifically
        if (axios.isAxiosError(error) && error.response?.status === 204) {
            const processError = new Error("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
            (processError as any).isProcessing = true;
            throw processError;
        }
        throw error;
    }
};

export const getTopPicksByDivision = async (season: number, division: string): Promise<{ account: Account, picks: number }[]> => {
    try {
        const response = await axios.get(`${url}/getTopPicksByDivision?season=${season}&division=${division}&amount=0`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
};

export const getTopSingleTeamPicks = async (
    season: number,
    division: string,
    limit = 10
  ): Promise<FantasyPickDTO[]> => {
    try {
      // Example endpoint: 
      //   /api/Fantasy/getTopPicksByDivisionSingleTeam?season=28&division=Invite&amount=10
      const response = await axios.get(`${url}/getTopPicksByDivisionSingleTeam`, {
        params: {
          season,
          division,
          amount: limit
        }
      });
      return response.data as FantasyPickDTO[];
    } catch (error) {
      throw error;
    }
  };



export const createFantasyTeam = async (token: string, fantasyTeam: FantasyTeam): Promise<boolean> => {
    // Remove the incomingTeamId field from players
    const sanitizedTeam = {
        ...fantasyTeam,
        players: fantasyTeam.players.map(({ incomingTeamId, ...rest }) => rest) // Exclude incomingTeamId
    };

    try {
        const response = await axios.post(`${url}/createFantasyTeam`, sanitizedTeam, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.status === 200;
    }
    catch (error) {
        // Attach the API's own explanation (e.g. signups are closed) so the page can show it rather
        // than a bare "something went wrong".
        if (axios.isAxiosError(error) && typeof error.response?.data === 'string') {
            (error as any).apiMessage = error.response.data;
        }
        throw error;
    }
}



export const getFantasyTeams = async (steamId: string): Promise<FantasyTeam[]> => {
    try {
        const response = await axios.get(`${url}/getFantasyTeams?steamId=${steamId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getTeamsUsingPlayer = async (steamId: string, season: number): Promise<FantasyTeam[]> => {
    try {
        const response = await axios.get(`${url}/getTeamsUsingPlayer?steamId=${steamId}&season=${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
};


export const checkPlayerHasFantasyTeam = async (steamId: string, season: number): Promise<boolean> => {
    const response = await fetch(`${url}/hasFantasyTeam?steamId=${steamId}&season=${season}`);
    const hasTeam = await response.json();
    return hasTeam;
};


export const getFantasySimpleTeams = async (steamId: string, season: number): Promise<FantasyTeam[]> => {
    try {
        const response = await axios.get(`${url}/getFantasyTeamsSeason?steamId=${steamId}&season=${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const updateFantasyTeam = async (token: string, fantasyTeam: FantasyTeam): Promise<boolean> => {
    try {
        // Remove the incomingTeamId field from players
        const sanitizedTeam = {
            ...fantasyTeam,
            players: fantasyTeam.players.map(({ incomingTeamId, ...rest }) => rest) // Exclude incomingTeamId
        };

        const response = await axios.post(`${url}/updateFantasyTeam`, sanitizedTeam, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.status === 200;
    }
    catch (error) {
        return false;
    }
}

export const deleteFantasyTeam = async (token: string, fantasyTeam: FantasyTeam): Promise<boolean> => {
    try {
        const response = await axios.delete(`${url}/deleteFantasyTeam`, {
            headers: {
                Authorization: `Bearer ${token}`
            },
            data: fantasyTeam
        });
        return response.status === 200;
    }
    catch (error) {
        return false;
    }
}

export const getAllTeams = async (season: number, division: string, group: string | null = null): Promise<FantasyStartTeam[]> => {
    try {
        const response = await axios.get(`${url}/getFantasyTeamsBySeasonAndDivision?season=${season}&division=${division}${group !== null ? `${group}` : ''}`);

        // Check if response status is 204 (No Content)
        if (response.status === 204) {
            const error = new Error("Data is still being processed. Please try again in a few minutes.");
            (error as any).isProcessing = true;
            throw error;
        }

        // Check if response.data is null or not an array
        if (!response.data || !Array.isArray(response.data)) {
            return []; // Return empty array if no teams found
        }

        // Map over teams to assign incomingTeamId to each player
        const teams = response.data.map((team: FantasyStartTeam) => {
            team.players.forEach(player => {
                player.incomingTeamId = team.id; // Assign the GUID from the FantasyStartTeam to the player
            });
            return team;
        });

        return teams;
    } catch (error) {
        // If it's an axios error with 204 status, handle it specifically
        if (axios.isAxiosError(error) && error.response?.status === 204) {
            const processError = new Error("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
            (processError as any).isProcessing = true;
            throw processError;
        }
        throw error;
    }
};

export interface TeamDisqualification {
    disqualified: boolean;
    reason: string | null;
}

// Whether a team is hidden from the leaderboard, and why. Without this the team just silently stops
// appearing on the scoreboard and the manager has no way to find out what rule they broke.
export const getTeamDisqualification = async (season: number, division: string, steamId: string): Promise<TeamDisqualification> => {
    try {
        const response = await axios.get(`${url}/getTeamDisqualification`, {
            params: { steamId, season, division }
        });
        return response.data ?? { disqualified: false, reason: null };
    } catch (error) {
        // A failed lookup must never hide the team page itself.
        return { disqualified: false, reason: null };
    }
};

export const getFantasyTeamRanking = async (season: number, division: string, steamId: string): Promise<number> => {
    try {
        const response = await axios.get(`${url}/getFantasyTeamRanking?season=${season}&division=${division}&steamId=${steamId}`);
        
        // Check if response status is 204 (No Content)
        if (response.status === 204) {
            const error = new Error("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
            (error as any).isProcessing = true;
            throw error;
        }
        
        return response.data;
    } catch (error) {
        // If it's an axios error with 204 status, handle it specifically
        if (axios.isAxiosError(error) && error.response?.status === 204) {
            const processError = new Error("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
            (processError as any).isProcessing = true;
            throw processError;
        }
        throw error;
    }
};



export const getFantasyLeaderboard = async (season: number, steamId: string | null): Promise<{ [division: string]: FantasyTeam[] }> => {
    try {
        const response = await axios.get(`${url}/getFantasyLeaderboard?season=${season}&steamId=${steamId}`);
        
        // Check if response status is 204 (No Content)
        if (response.status === 204) {
            const error = new Error("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
            (error as any).isProcessing = true;
            throw error;
        }
        
        return response.data;
    }
    catch (error) {
        // If it's an axios error with 204 status, handle it specifically
        if (axios.isAxiosError(error) && error.response?.status === 204) {
            const processError = new Error("Data er stadig ved at blive behandlet. Prøv igen om et par minutter.");
            (processError as any).isProcessing = true;
            throw processError;
        }
        throw error;
    }
}

// amount <= 0 returns every picked player; the default of 5 matches the original preview behavior.
export const getMostPickedPlayers = async (season: number, amount: number = 5): Promise<{ account: Account, picks: number }[]> => {
    try {
        const response = await axios.get(`${url}/getPopularPicks?season=${season}&amount=${amount}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getSeasons = async (): Promise<number[]> => {
    try {
        const response = await axios.get(`${url}/getFantasySeasons`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const isCurrentSeasonLocked = async (): Promise<boolean> => {
    try {
        const response = await axios.get(`${url}/isCurrentSeasonLocked`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

// The current season's lock state plus the deadline it auto-locks at. serverTime lets the client
// correct for a skewed local clock when counting down.
export interface FantasyLockStatus {
    season: number;
    locked: boolean;
    lockTime: string;
    serverTime: string;
}

export const getFantasyLockStatus = async (): Promise<FantasyLockStatus> => {
    try {
        const response = await axios.get(`${url}/getFantasyLockStatus`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}